"use client";

import Image, { type StaticImageData } from "next/image";
import { motion, useScroll, useTransform } from "framer-motion";
import { MonicateringTextLink } from "@/components/ui/MonicateringTextLink";
import { CONTACT_FORM_HASH_HREF } from "@/lib/contact-form";
import { useEffect, useRef, useState } from "react";

type ImageProps = {
  src: string | StaticImageData;
  alt?: string;
};

type ButtonConfig = {
  title: string;
  href?: string;
};

export type HeroAboutUsClientProps = {
  heading: string;
  description: string;
  buttons: ButtonConfig[];
  image: ImageProps;
  video?: string;
  videoSrc?: string;
};

/**
 * Desktop: sticky hero + parallax (Framer). Mobile: statyczny full-bleed.
 * Wideo pauzuje przy reduced-motion / poza viewportem.
 */
export function HeroAboutUsClient({
  heading,
  description,
  buttons,
  image,
  video,
  videoSrc,
}: HeroAboutUsClientProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const preferredVideo = video ?? videoSrc ?? "/v1.mp4";
  const resolvedVideo = preferredVideo.includes("sanity.io")
    ? "/v1.mp4"
    : preferredVideo;

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const videoEl = videoRef.current;
    const section = sectionRef.current;
    if (!videoEl || !section) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reduceMotion) {
      videoEl.pause();
      return;
    }

    const sync = () => {
      if (document.hidden) {
        videoEl.pause();
        return;
      }
      void videoEl.play().catch(() => {});
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !document.hidden) {
          void videoEl.play().catch(() => {});
        } else {
          videoEl.pause();
        }
      },
      { rootMargin: "80px 0px", threshold: 0 },
    );
    io.observe(section);
    document.addEventListener("visibilitychange", sync);

    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", sync);
    };
  }, [resolvedVideo]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // Keep the original scroll behavior 1:1 from the reference.
  const containerMotion = {
    y: useTransform(scrollYProgress, [0, 0.4], ["0vh", "-5vh"]),
    width: useTransform(scrollYProgress, [0, 0.4], ["90%", "100%"]),
    height: useTransform(scrollYProgress, [0, 0.4], ["90vh", "100vh"]),
  };

  const imageTranslate = {
    y: useTransform(scrollYProgress, [0.4, 1], ["0%", "70%"]),
  };

  const videoDialogTranslate = {
    y: useTransform(scrollYProgress, [0.3, 0.4], ["0%", "200%"]),
  };

  return (
    <section
      ref={sectionRef}
      id="o-nas-hero"
      data-no-image-reveal-scope
      className="relative flex h-[100vh] flex-col items-center md:h-[150vh]"
    >
      <motion.div
        className="sticky top-[5vh] z-10 mb-[-5vh] mt-[5vh] flex h-[90vh] w-[90%] flex-col items-start justify-center overflow-hidden"
        style={isDesktop ? containerMotion : undefined}
      >
        <div className="px-[3%] py-16 md:py-24 lg:py-28">
          <div className="container relative z-10 max-w-lg">
            <h1 className="mb-5 font-season-serif text-4xl leading-[1.05] tracking-[-0.02em] md:mb-6 md:text-7xl">
              {heading}
            </h1>
            <p className="max-w-md whitespace-pre-line md:text-md">
              {description}
            </p>
            <div className="mt-6 flex flex-wrap gap-4 md:mt-8">
              {buttons.map((button, index) => (
                <MonicateringTextLink
                  key={`${button.title}-${index}`}
                  href={button.href ?? CONTACT_FORM_HASH_HREF}
                  tone="onDark"
                >
                  {button.title}
                </MonicateringTextLink>
              ))}
            </div>
          </div>
        </div>

        <motion.div
          className="absolute inset-0 z-0"
          style={isDesktop ? imageTranslate : undefined}
        >
          <Image
            src={image.src}
            alt={image.alt ?? "Monicatering"}
            fill
            priority
            sizes="100vw"
            quality={80}
            className="size-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" aria-hidden />
        </motion.div>

        <motion.div
          className="absolute bottom-[5%] right-[5%] hidden w-full md:block md:max-w-[13rem] lg:max-w-[14rem]"
          style={isDesktop ? videoDialogTranslate : undefined}
        >
          <video
            ref={videoRef}
            className="size-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden
          >
            <source src={resolvedVideo} type="video/mp4" />
          </video>
        </motion.div>
      </motion.div>

      <div className="absolute inset-0 -z-10 mt-[100vh]" aria-hidden />
    </section>
  );
}
